import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { faDown } from "@performyard/fontawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@performyard/fontawesome/react-fontawesome";
import { ButtonStandard, toast } from "@performyard/py-components";

import RadialProgress from "../../../components.smart/DownloadButton/RadialProgress";

import {
	getIsJobCompleted,
	getIsJobRunningID,
	getJobErrorMessage,
	getJobFileDownload,
	getJobProgress,
} from "../../../selectors/jobs";

import pubSub from "../../../utils/PubSub";

type DirectoryCsvDownloadButtonProps = {
	onStart: () => Promise<string>;
};

const JOB_REGISTRATION_TIMEOUT_MS = 30_000;
/** Grace for a URL that lands a poll after the job flips finished. */
const COMPLETED_WITHOUT_URL_GRACE_MS = 2_000;

const DirectoryCsvDownloadButton = ({ onStart }: DirectoryCsvDownloadButtonProps): React.ReactElement => {
	const [jobId, setJobId] = useState<string | null>(null);
	const [isInitializing, setIsInitializing] = useState(false);
	const downloadedJobIdRef = useRef<string | null>(null);
	const attemptIdRef = useRef(0);
	const registrationTimeoutRef = useRef<number | null>(null);

	const progress = useSelector((state: any) => getJobProgress(state, jobId));
	const isRunning = useSelector((state: any) => getIsJobRunningID(state, jobId));
	const isCompleted = useSelector((state: any) => getIsJobCompleted(state, jobId));
	const downloadUrl = useSelector((state: any) => getJobFileDownload(state, jobId)) as string | null;
	const errorMessage = useSelector((state: any) => getJobErrorMessage(state, jobId)) as string | null;
	const isDownloading = isInitializing || jobId !== null;

	const clearRegistrationTimeout = useCallback(() => {
		if (registrationTimeoutRef.current !== null) {
			window.clearTimeout(registrationTimeoutRef.current);
			registrationTimeoutRef.current = null;
		}
	}, []);

	const resetPendingState = useCallback(() => {
		attemptIdRef.current += 1;
		clearRegistrationTimeout();
		setIsInitializing(false);
		setJobId(null);
	}, [clearRegistrationTimeout]);

	useEffect(() => clearRegistrationTimeout, [clearRegistrationTimeout]);

	useEffect(() => {
		if (isRunning) {
			clearRegistrationTimeout();
			setIsInitializing(false);
		}
	}, [clearRegistrationTimeout, isRunning]);

	useEffect(() => {
		if (!jobId || !errorMessage) return;
		toast.error(errorMessage);
		resetPendingState();
	}, [errorMessage, jobId, resetPendingState]);

	useEffect(() => {
		if (!jobId || !isCompleted || !downloadUrl || downloadedJobIdRef.current === jobId) return;

		downloadedJobIdRef.current = jobId;
		window.open(downloadUrl, "_blank");
		resetPendingState();
	}, [downloadUrl, isCompleted, jobId, resetPendingState]);

	// A finished job can carry no URL: user_jobs patches redirectURL onto the first job of each
	// message only, so a second job in flight takes it. Neither effect above fires, the job drops
	// out of polling a second later, and nothing else clears jobId — so the button would stay
	// disabled until a reload. The legacy DownloadButton never disabled, so a retry was free.
	useEffect(() => {
		if (!jobId || !isCompleted || downloadUrl || errorMessage) return;

		const timeout = window.setTimeout(() => {
			resetPendingState();
			toast.error("The CSV finished without a download link. Please try again.");
		}, COMPLETED_WITHOUT_URL_GRACE_MS);

		return () => window.clearTimeout(timeout);
	}, [downloadUrl, errorMessage, isCompleted, jobId, resetPendingState]);

	const handleClick = async () => {
		if (isDownloading || registrationTimeoutRef.current !== null) return;

		const attemptId = attemptIdRef.current + 1;
		attemptIdRef.current = attemptId;
		setIsInitializing(true);
		pubSub.publish("check async frequency", { frequency: 500 });
		registrationTimeoutRef.current = window.setTimeout(() => {
			if (attemptIdRef.current !== attemptId) return;
			resetPendingState();
			toast.error("The CSV download did not start. Please try again.");
		}, JOB_REGISTRATION_TIMEOUT_MS);

		try {
			const nextJobId = await onStart();
			if (attemptIdRef.current !== attemptId) return;
			setJobId(nextJobId);
		} catch {
			if (attemptIdRef.current !== attemptId) return;
			resetPendingState();
			toast.error("There was an error starting the CSV download.");
		}
	};

	return (
		<ButtonStandard
			variant="outline"
			color="neutral"
			disabled={isDownloading}
			dangerouslySetClassName="w-auto"
			onClick={handleClick}
		>
			<div className="flex items-center gap-2" data-testid="download-csv">
				<div className="relative h-[22px] w-[22px] rounded-full">
					<div
						className={`absolute inset-0 z-10 flex items-center justify-center text-xs ${
							isDownloading ? "text-primary-5" : "text-neutral-3"
						}`}
					>
						<FontAwesomeIcon icon={faDown} />
					</div>
					<RadialProgress size={22} strokeWidth={2} percentage={isRunning ? progress * 100 : 0} />
				</div>

				<span>{isDownloading ? "Downloading..." : "Download CSV"}</span>
			</div>
		</ButtonStandard>
	);
};

export default DirectoryCsvDownloadButton;
